import L from "leaflet";

import type { ApiClient } from "../api";
import { friendlyMessage } from "../api";

const NOTES_MAX = 500;

export interface AddCtx {
  api: ApiClient;
  map: L.Map;
  panelRoot: HTMLElement;
  notify: (msg: string) => void;
  onCreated: () => void;
}

function pinIcon(): L.DivIcon {
  return L.divIcon({
    className: "pin pin-drop",
    html: '<span class="pin-glyph">📍</span>',
    iconSize: [40, 40],
    iconAnchor: [20, 36],
  });
}

export function openAddPanel(ctx: AddCtx): void {
  ctx.panelRoot.innerHTML = "";

  // Drop the pin at the current map center immediately so the user has
  // something to drag, instead of having to tap a map area that's mostly
  // covered by this bottom sheet.
  const start = ctx.map.getCenter();
  let coords = { lat: start.lat, lon: start.lng };
  const dropMarker = L.marker(start, {
    draggable: true,
    autoPan: true,
    icon: pinIcon(),
  }).addTo(ctx.map);
  dropMarker.on("dragend", () => {
    const ll = dropMarker.getLatLng();
    coords = { lat: ll.lat, lon: ll.lng };
  });

  // Tap on a visible part of the map to relocate the pin (alternative to drag).
  const onMapClick = (e: L.LeafletMouseEvent): void => {
    dropMarker.setLatLng(e.latlng);
    coords = { lat: e.latlng.lat, lon: e.latlng.lng };
  };
  ctx.map.on("click", onMapClick);

  const sheet = document.createElement("section");
  sheet.className = "panel sheet add-panel";
  sheet.setAttribute("role", "dialog");
  sheet.setAttribute("aria-label", "Add a toilet");

  const closeBtn = document.createElement("button");
  closeBtn.type = "button";
  closeBtn.className = "panel-close";
  closeBtn.setAttribute("aria-label", "Close");
  closeBtn.textContent = "✕";
  sheet.appendChild(closeBtn);

  const title = document.createElement("h2");
  title.textContent = "Add a toilet";
  sheet.appendChild(title);

  const explain = document.createElement("p");
  explain.className = "muted";
  explain.textContent = "Drag the pin (or tap the map) to position. Then save.";
  sheet.appendChild(explain);

  // Visibility radios
  const visGroup = document.createElement("fieldset");
  visGroup.className = "vis-group";
  const legend = document.createElement("legend");
  legend.textContent = "Visibility";
  visGroup.appendChild(legend);

  const personalLabel = document.createElement("label");
  const personalInput = document.createElement("input");
  personalInput.type = "radio";
  personalInput.name = "is_public";
  personalInput.value = "personal";
  personalInput.checked = true;
  personalLabel.appendChild(personalInput);
  personalLabel.appendChild(document.createTextNode(" Personal (only you)"));
  visGroup.appendChild(personalLabel);

  const publicLabel = document.createElement("label");
  const publicInput = document.createElement("input");
  publicInput.type = "radio";
  publicInput.name = "is_public";
  publicInput.value = "public";
  publicLabel.appendChild(publicInput);
  publicLabel.appendChild(document.createTextNode(" Public (anyone can use)"));
  visGroup.appendChild(publicLabel);

  const radioCopy = document.createElement("p");
  radioCopy.className = "muted";
  radioCopy.textContent = "Public means anyone can use this. Personal means it's just for you.";
  visGroup.appendChild(radioCopy);

  sheet.appendChild(visGroup);

  // Notes
  const notesLabel = document.createElement("label");
  notesLabel.className = "notes-label";
  const notesText = document.createElement("span");
  notesText.textContent = "Notes (optional)";
  notesLabel.appendChild(notesText);
  const notes = document.createElement("textarea");
  notes.rows = 3;
  notes.maxLength = NOTES_MAX;
  notes.placeholder = "paid 1 EUR, code at counter, behind the bookshop...";
  const counter = document.createElement("span");
  counter.className = "counter";
  counter.textContent = `0 / ${NOTES_MAX}`;
  notes.addEventListener("input", () => {
    counter.textContent = `${[...notes.value].length} / ${NOTES_MAX}`;
  });
  notesLabel.appendChild(notes);
  notesLabel.appendChild(counter);
  sheet.appendChild(notesLabel);

  // Action row: Cancel + Save
  const actions = document.createElement("div");
  actions.className = "panel-actions";

  const cancelBtn = document.createElement("button");
  cancelBtn.type = "button";
  cancelBtn.className = "btn-secondary";
  cancelBtn.textContent = "Cancel";
  actions.appendChild(cancelBtn);

  const submitBtn = document.createElement("button");
  submitBtn.type = "button";
  submitBtn.className = "btn-primary";
  submitBtn.textContent = "Save pin";
  actions.appendChild(submitBtn);

  sheet.appendChild(actions);
  ctx.panelRoot.appendChild(sheet);

  const cleanup = (): void => {
    ctx.map.off("click", onMapClick);
    ctx.map.removeLayer(dropMarker);
    ctx.panelRoot.innerHTML = "";
  };

  closeBtn.addEventListener("click", cleanup);
  cancelBtn.addEventListener("click", cleanup);

  submitBtn.addEventListener("click", async () => {
    submitBtn.disabled = true;
    cancelBtn.disabled = true;
    try {
      const isPublic = publicInput.checked;
      const trimmed = notes.value.trim();
      await ctx.api.createToilet({
        lat: coords.lat,
        lon: coords.lon,
        is_public: isPublic,
        ...(trimmed ? { notes: trimmed } : {}),
      });
      ctx.notify("Saved.");
      cleanup();
      ctx.onCreated();
    } catch (err) {
      ctx.notify(friendlyMessage(err));
      submitBtn.disabled = false;
      cancelBtn.disabled = false;
    }
  });
}
